import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogoMark } from "@/components/Logo";

const CONTAINER_IMG =
  "https://images.unsplash.com/photo-1605732563938-8f8d4e7ad651?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxzaGlwcGluZyUyMGNvbnRhaW5lcnMlMjBjYXJnbyUyMGFic3RyYWN0fGVufDB8fHx8MTc4ODc0MDQzOXww&ixlib=rb-4.1.0&q=85";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function goToGoogle() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthPage({ mode = "login" }) {
  const isRegister = mode === "register";
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("invite") || "";
  const { login, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegister) await register(email, password, name, inviteCode || undefined);
      else await login(email, password);
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={CONTAINER_IMG} alt="Shipping containers" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <LogoMark size={48} className="text-white" />
            <div className="leading-none">
              <div className="font-heading text-2xl font-black tracking-tight">OUT <span className="text-orange-500">&amp;</span> IN</div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-white/60 mt-1.5">Dispatch Ops</div>
            </div>
          </div>
          <div className="space-y-4 max-w-md">
            <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">
              Logistics Operations
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-tight">
              Move goods with clarity, not chaos.
            </h1>
            <p className="text-sm text-white/70 leading-relaxed">
              Log every pickup and delivery. Notify suppliers and couriers instantly. Keep every
              PO, invoice and job reference attached to the movement.
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-white/40">
            &copy; {new Date().getFullYear()} Out &amp; In
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md space-y-8">
          <div className="flex items-center gap-3 lg:hidden mb-2">
            <LogoMark size={40} className="text-foreground" />
            <div className="font-heading text-xl font-black tracking-tight">
              OUT <span className="text-orange-600">&amp;</span> IN
            </div>
          </div>
          <div className="hidden lg:flex items-center gap-3">
            <LogoMark size={44} className="text-foreground" />
            <div className="leading-none">
              <div className="font-heading text-xl font-black tracking-tight">
                OUT <span className="text-orange-600">&amp;</span> IN
              </div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground mt-1.5">Dispatch Ops</div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">
              {isRegister ? "Create account" : "Welcome back"}
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">
              {isRegister ? "Start dispatching." : "Sign in to your portal."}
            </h2>
            {isRegister && inviteCode && (
              <div data-testid="invite-banner" className="mt-3 border-2 border-orange-600 bg-orange-50 dark:bg-orange-950 px-3 py-2 text-xs">
                <span className="uppercase tracking-[0.15em] font-bold text-orange-700 dark:text-orange-300">Team invite</span>
                <span className="ml-2 font-mono">{inviteCode}</span>
                <span className="ml-2 text-muted-foreground">— you'll join this team on sign-up.</span>
              </div>
            )}
          </div>

          <Button
            data-testid="google-signin-button"
            type="button"
            onClick={goToGoogle}
            variant="outline"
            className="w-full h-11 border-2 border-foreground rounded-none hover:bg-foreground hover:text-background transition-colors duration-200"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 mr-2" aria-hidden>
              <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.66 4.1-5.5 4.1-3.31 0-6-2.74-6-6.1s2.69-6.1 6-6.1c1.88 0 3.14.8 3.86 1.5l2.63-2.53C16.9 3.5 14.7 2.5 12 2.5 6.75 2.5 2.5 6.75 2.5 12s4.25 9.5 9.5 9.5c5.48 0 9.11-3.85 9.11-9.27 0-.63-.07-1.1-.16-1.53H12z" />
            </svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em] font-bold">Full name</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="h-11 rounded-none border-2 focus-visible:ring-orange-500"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] font-bold">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 rounded-none border-2 focus-visible:ring-orange-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] font-bold">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="auth-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11 rounded-none border-2 focus-visible:ring-orange-500"
              />
            </div>

            {error && (
              <div data-testid="auth-error" className="border-2 border-red-600 bg-red-50 text-red-700 text-sm p-3">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid={isRegister ? "register-submit-button" : "login-submit-button"}
              disabled={loading}
              className="w-full h-11 rounded-none bg-orange-600 hover:bg-orange-700 text-white transition-colors duration-200 font-bold uppercase tracking-[0.15em] text-xs"
            >
              {loading ? "Please wait..." : isRegister ? "Create account" : "Sign in"}
            </Button>
          </form>

          <div className="text-sm text-muted-foreground">
            {isRegister ? (
              <>Already have an account?{" "}
                <Link data-testid="link-to-login" to="/login" className="text-orange-600 hover:underline font-medium">Sign in</Link>
              </>
            ) : (
              <>New here?{" "}
                <Link data-testid="link-to-register" to="/register" className="text-orange-600 hover:underline font-medium">Create an account</Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
